import { ReactNode } from 'react'
import { EyeIcon, TrashIcon } from '@heroicons/react/24/outline'

/**
 * Row actions for list tables, matching the profile list (AccountRow /
 * DashboardButton). Icon-only and borderless until hover, so a cluster of
 * actions reads as one quiet unit at the end of the row rather than as several
 * competing buttons — which is what lets the row's own content carry the weight.
 */
export const ROW_ACTION_ICON_CLASSES =
    'inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-ink-600 hover:border-ink-200 hover:bg-ink-100 focus:outline-none focus:ring-2 focus:ring-arwen-500'

/** Wraps a row's actions so they sit right-aligned as a single cluster. */
export function RowActions({ children }: { children: ReactNode }) {
    return <div className="flex items-center justify-end gap-1">{children}</div>
}

/**
 * Screen-reader-only header for an actions column — the icons are self-evident
 * visually, so the column heading would only add noise.
 */
export function rowActionsHeader() {
    return <span className="sr-only">Actions</span>
}

/** The eye icon means "view" everywhere in Arwen — see the profile list. */
export function ViewRowAction({ href, label }: { href: string; label: string }) {
    return (
        <a href={href} className={ROW_ACTION_ICON_CLASSES} title={label} aria-label={label}>
            <EyeIcon className="h-5 w-5" aria-hidden="true" />
        </a>
    )
}

export function DeleteRowAction({
    onClick,
    label,
    disabled
}: {
    onClick: () => void
    label: string
    disabled?: boolean
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`${ROW_ACTION_ICON_CLASSES} ${
                disabled ? 'cursor-not-allowed text-ink-300' : 'hover:text-arwen-600'
            }`}
            title={label}
            aria-label={label}
        >
            <TrashIcon className="h-5 w-5" aria-hidden="true" />
        </button>
    )
}
